// ./src/components/generic/CheckableMultiSelect/CheckableMultiSelect.tsx
//
// A compact multi-select rendered as a single-line trigger + a checklist popover
// (not a growing pill field). The trigger shows the one selected label or an
// "N selected" summary, so the control keeps its height no matter how many items
// are picked. `trapFocus={false}` keeps it usable inside a Drawer/Modal focus
// trap. Use anywhere a set of values is chosen from a modest list (filters, etc.).
//
// Two modes. By default it holds the whole list and filters it in the browser. Pass `onSearchChange`
// and the CALLER owns the query — rows come from a server lookup a page at a time, the local filter is
// off, and `loading` / `footer` say what the list is. The machinery is `usePickerSearch`; the reason
// the local filter must not run over server results is on the `onSearchChange` prop.
//
// ⚠ ON A PHONE the checklist is the full-screen picker (OptionPickerModal, multiple), as every selector is (owner,
//   2026-10-09): the filters sit in a bottom-sheet drawer on a phone, and a popover list inside it was the same small,
//   jumping box the dropdowns were.

import React, { useMemo, useState } from "react";
import {
  Input,
  Popover,
  ScrollArea,
  Checkbox,
  TextInput,
  Group,
  Text,
  Stack,
  Divider,
  UnstyledButton,
  Button,
  Box,
  Loader,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppIcon } from "../AppIcon";
import { OptionPickerModal, usesPhonePicker } from "../form";

export interface CheckableMultiSelectOption {
  value: string;
  label: string;
}

export interface CheckableMultiSelectProps {
  data: CheckableMultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  /** Show a search box in the dropdown (auto-on when data is long). */
  searchable?: boolean;
  withinPortal?: boolean;
  /** Trigger height, matching Mantine input sizes (default 'md'). */
  size?: string;
  /**
   * SERVER-SIDE SEARCH. When this is given the caller owns the query: every keystroke is reported and
   * `data` is rendered exactly as handed over.
   *
   * ⚠⚠ THE LOCAL FILTER IS SWITCHED OFF, and that is not a shortcut — it is the point. The server
   * matches on columns the label does not show (an email, a phone, a ticket code), so filtering the
   * results again by their LABELS silently drops the very rows the query found: search a guest's email
   * address and the row whose label is just their name disappears. The list would look empty while the
   * server was answering perfectly.
   */
  onSearchChange?: (value: string) => void;
  /** The query, when `onSearchChange` is in play (controlled). */
  searchValue?: string;
  /** Spinner in the search box — a lookup is in flight. */
  loading?: boolean;
  /** Rendered under the option list: a truncation note, a failure, a hint. */
  footer?: React.ReactNode;
  /** The server's refusal, at the input. */
  error?: React.ReactNode;
}

export const CheckableMultiSelect: React.FC<CheckableMultiSelectProps> = ({
  data,
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  searchable,
  withinPortal = true,
  size = "md",
  onSearchChange,
  searchValue,
  loading = false,
  footer,
  error,
}) => {
  const { t } = useTranslation(["common"]);
  const { isMobile } = useAppEnvironment();
  const phonePicker = usesPhonePicker({ isMobile, disabled });
  const [opened, setOpened] = useState(false);
  const [localSearch, setLocalSearch] = useState("");

  const serverSearch = !!onSearchChange;
  const search = serverSearch ? searchValue ?? "" : localSearch;
  const setSearch = (next: string) => {
    if (serverSearch) {
      onSearchChange?.(next);

      return;
    }

    setLocalSearch(next);
  };

  /**
   * ⚠ ALWAYS SHOWN in server mode. The auto-rule below counts the rows currently in `data`, and in
   * server mode a SHORT list is what a narrow query produces — so the box that produced it would
   * disappear the moment it worked, stranding the host in their own search with no way to widen it.
   */
  const showSearch = serverSearch || (searchable ?? data.length > 8);

  const summary = useMemo(() => {
    if (value.length === 0) return null;
    if (value.length === 1) return data.find((d) => d.value === value[0])?.label ?? t("common:selection.oneSelected", { defaultValue: "1 selected" });
    return t("common:selection.nSelected", { defaultValue: "{{n}} selected", n: value.length });
  }, [value, data, t]);

  const filtered = useMemo(() => {
    // ⚠ In server mode `data` IS the answer — see `onSearchChange` for why filtering it again is a bug.
    if (serverSearch) {
      return data;
    }

    const q = search.toLowerCase().trim();
    return q ? data.filter((d) => d.label.toLowerCase().includes(q)) : data;
  }, [data, search, serverSearch]);

  const toggle = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  const trigger = (
    <Input
      error={!!error}
      component="button"
      type="button"
      pointer
      size={size}
      disabled={disabled}
      onClick={() => setOpened((o) => !o)}
      rightSectionPointerEvents="none"
      rightSection={<AppIcon iconName="Selector" size={16} />}
    >
      {summary ? <Text size="md" truncate>{summary}</Text> : <Input.Placeholder>{placeholder}</Input.Placeholder>}
    </Input>
  );

  if (phonePicker) {
    return (
      <Input.Wrapper label={label} error={error}>
        {trigger}
        <OptionPickerModal
          opened={opened}
          onClose={() => setOpened(false)}
          title={label ?? placeholder}
          data={data}
          multiple
          values={value}
          onToggle={toggle}
          searchable={showSearch}
          searchValue={serverSearch ? search : undefined}
          onSearchChange={serverSearch ? setSearch : undefined}
          loading={loading}
          nothingFoundMessage={
            loading && serverSearch ? t("common:searching", { defaultValue: "Searching…" }) : t("common:noResults", { defaultValue: "No results" })
          }
          // ⚠ Always there, disabled while nothing is picked: appearing on the first tick pushed every row down under
          //   the finger, and the next tap landed on the wrong one.
          header={
            <Group justify="space-between" mt="xs" wrap="nowrap">
              <Box style={{ flex: 1, minWidth: 0 }}>{footer}</Box>
              <Button variant="subtle" size="md" color="gray" disabled={value.length === 0} onClick={() => onChange([])}>
                {t("common:buttons.clear", { defaultValue: "Clear" })}
              </Button>
            </Group>
          }
          doneLabel={t("common:buttons.done", { defaultValue: "Done" })}
        />
      </Input.Wrapper>
    );
  }

  return (
    <Input.Wrapper label={label} error={error}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        width="target"
        shadow="md"
        withinPortal={withinPortal}
        trapFocus={false}
        middlewares={{ flip: true, shift: true }}
      >
        <Popover.Target>{trigger}</Popover.Target>
        <Popover.Dropdown p={0}>
          <Stack gap={0}>
            {showSearch && (
              <Box p="xs">
                <TextInput
                  size="md"
                  placeholder={t("common:search", { defaultValue: "Search…" })}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  leftSection={<AppIcon iconName="Search" size={14} />}
                  rightSection={loading ? <Loader size="xs" /> : undefined}
                />
              </Box>
            )}
            <ScrollArea.Autosize mah={260} type="scroll">
              <Stack gap={0} p={4}>
                {filtered.length === 0 ? (
                  <Text size="sm" c="dimmed" p="sm">
                    {/* ⚠ "Still looking" is not "nothing matches" — saying the latter while a lookup is in
                        flight tells the host their person does not exist, a moment before they appear. */}
                    {loading && serverSearch
                      ? t("common:searching", { defaultValue: "Searching…" })
                      : t("common:noResults", { defaultValue: "No results" })}
                  </Text>
                ) : (
                  filtered.map((o) => {
                    const checked = value.includes(o.value);
                    return (
                      <UnstyledButton
                        key={o.value}
                        onClick={() => toggle(o.value)}
                        p="xs"
                        style={{ borderRadius: 6 }}
                        bg={checked ? "var(--mantine-color-brand-light)" : undefined}
                      >
                        <Group gap="sm" wrap="nowrap">
                          <Checkbox checked={checked} readOnly tabIndex={-1} size="md" />
                          <Text size="md">{o.label}</Text>
                        </Group>
                      </UnstyledButton>
                    );
                  })
                )}
              </Stack>
            </ScrollArea.Autosize>
            {/* A truncation note or a failed lookup — under the rows it is about, inside the dropdown. */}
            {footer ? (
              <>
                <Divider />
                <Box p="xs">{footer}</Box>
              </>
            ) : null}
            {value.length > 0 && (
              <>
                <Divider />
                <Group justify="space-between" p="xs">
                  <Button variant="subtle" size="md" color="gray" onClick={() => onChange([])}>
                    {t("common:buttons.clear", { defaultValue: "Clear" })}
                  </Button>
                  <Text size="xs" c="dimmed">
                    {t("common:selection.nSelected", { defaultValue: "{{n}} selected", n: value.length })}
                  </Text>
                </Group>
              </>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Input.Wrapper>
  );
};
